<template>
    <el-header>
        <div class="nav-container">
            <el-menu
                :default-active="activeMenu"
                class="nav-menu"
                mode="horizontal"
                :ellipsis="false"
                background-color="var(--bg-card)"
                text-color="var(--text-secondary)"
                active-text-color="var(--color-primary)"
                router>
                
                <div class="nav-logo-wrap">
                    <span class="nav-logo-text">Xtool</span>
                </div>

                <el-menu-item index="/json">JSON</el-menu-item>
                <el-menu-item index="/diffCheck">Diff</el-menu-item>
                <el-menu-item index="/encode">Encode</el-menu-item>
                <el-menu-item index="/markdown">Markdown</el-menu-item>
                
                <el-sub-menu index="image" popper-class="nav-submenu-popup">
                    <template #title>ImageX</template>
                    <el-menu-item index="/image/colorPick">Color Pick</el-menu-item>
                    <el-menu-item index="/image/resize">Image Resize</el-menu-item>
                    <el-menu-item index="/image/removeBg">Remove Background</el-menu-item>
                </el-sub-menu>
                
                <el-sub-menu index="tools" popper-class="nav-submenu-popup">
                    <template #title>Tools</template>
                    <el-menu-item index="/timerBoy">Timer</el-menu-item>
                    <el-menu-item index="/ipDetect">IP Fetch</el-menu-item>
                    <el-menu-item index="/woodenFish">Wooden Fish</el-menu-item>
                </el-sub-menu>
                
                <!-- Wiki requires login -->
                <el-menu-item index="/topNews">Global News</el-menu-item>
                <el-menu-item v-if="isLoggedIn" index="/strategyHub">Strategy Hub</el-menu-item>
                <el-menu-item v-if="isLoggedIn" index="/wiki">Wiki</el-menu-item>
                <el-menu-item v-if="isLoggedIn" index="/books">Books</el-menu-item>
                <el-menu-item index="/whiteboard">Whiteboard</el-menu-item>
            </el-menu>
            
            <!-- User actions - right side -->
            <div class="user-actions">
                <template v-if="isLoggedIn">
                    <el-dropdown trigger="click" @command="handleCommand">
                        <div class="user-trigger">
                            <span class="avatar">{{ usernameInitial }}</span>
                        </div>
                        <template #dropdown>
                            <el-dropdown-menu class="user-dropdown">
                                <div class="user-info">
                                    <span class="avatar-large">{{ usernameInitial }}</span>
                                    <div class="user-details">
                                        <span class="username">{{ username }}</span>
                                        <span class="role">{{ userRole }}</span>
                                    </div>
                                </div>
                                <el-dropdown-item divided command="logout">
                                    <span class="logout-icon">⏻</span>
                                    Log Out
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </template>
                <template v-else>
                    <el-button type="primary" size="small" @click="goToLogin">
                        Login
                    </el-button>
                </template>
            </div>
        </div>
    </el-header>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isAuthenticated, getUser, clearAuth, getToken } from '../utils/auth';
import { logout } from '../api/auth';

export default {
    name: 'MainNavbar',
    setup() {
        const route = useRoute();
        const router = useRouter();
        const loggingOut = ref(false);
        const loggedIn = ref(isAuthenticated());

        const updateAuthState = () => {
            loggedIn.value = isAuthenticated();
        };

        const handleStorageChange = (e) => {
            if (e.key === 'xtool_token') {
                updateAuthState();
            }
        };

        onMounted(() => {
            window.addEventListener('storage', handleStorageChange);
            window.addEventListener('auth-change', updateAuthState);
        });

        onUnmounted(() => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('auth-change', updateAuthState);
        });

        const activeMenu = computed(() => {
            const path = route.path;
            if (path.startsWith('/encode')) return '/encode';
            if (path.startsWith('/diffCheck')) return '/diffCheck';
            if (path.startsWith('/image/')) return `/image/${path.split('/')[2]}`;
            if (path.startsWith('/wiki')) return '/wiki';
            
            const directPaths = ['/json', '/markdown', '/topNews', '/timerBoy', '/ipDetect', '/woodenFish', '/strategyHub', '/whiteboard', '/books'];
            for (const p of directPaths) {
                if (path === p || path.startsWith(p + '/')) return p;
            }
            return '/';
        });

        const isLoggedIn = computed(() => loggedIn.value);
        
        const username = computed(() => {
            const user = getUser();
            return user ? user.username : '';
        });

        const usernameInitial = computed(() => {
            const user = getUser();
            return user && user.username ? user.username.charAt(0).toUpperCase() : 'U';
        });

        const userRole = computed(() => {
            const user = getUser();
            return user && user.role ? user.role : 'user';
        });

        const goToLogin = () => {
            router.push('/userLogin');
        };

        const handleCommand = async (command) => {
            if (command === 'logout') {
                await handleLogout();
            }
        };

        const handleLogout = async () => {
            if (loggingOut.value) return;
            
            loggingOut.value = true;
            try {
                const token = getToken();
                if (token) {
                    await logout(token);
                }
            } catch (e) {
                console.error('Logout API error:', e);
            } finally {
                clearAuth();
                loggedIn.value = false;
                loggingOut.value = false;
                window.dispatchEvent(new CustomEvent('auth-change'));
                router.push('/');
            }
        };

        return { 
            activeMenu, 
            isLoggedIn, 
            username,
            usernameInitial,
            userRole,
            loggingOut,
            goToLogin,
            handleCommand
        };
    },
};
</script>

<style scoped>
.nav-container {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    margin: 0 auto;
    padding: 0 var(--spacing-lg);
}

.nav-menu {
    flex: 1;
    display: flex;
    align-items: center;
    height: 100%;
}

.nav-logo-wrap {
    margin-right: auto;
    display: flex;
    align-items: center;
}

.nav-logo-text {
    font-family: var(--font-heading);
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: 0.08em;
    padding: 2px 8px;
    border: 2px solid var(--text-primary);
}

.user-actions {
    display: flex;
    align-items: center;
    padding-left: 16px;
}

.user-trigger {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.avatar {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-xs);
    background: var(--text-primary);
    color: var(--bg-card);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    transition: transform var(--transition-fast);
}

.user-trigger:hover .avatar {
    transform: scale(1.05);
}

:deep(.nav-menu > .el-menu-item) {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-secondary);
    height: 56px;
    line-height: 56px;
    padding: 0 14px;
    border-radius: var(--radius-md);
    transition: all var(--transition-fast);
}

:deep(.nav-menu > .el-menu-item:hover) {
    color: var(--color-primary);
    background-color: var(--bg-hover);
}

:deep(.nav-menu > .el-menu-item.is-active) {
    color: var(--color-primary) !important;
    background-color: var(--bg-hover) !important;
}

:deep(.nav-menu > .el-sub-menu > .el-sub-menu__title) {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-secondary);
    height: 56px;
    line-height: 56px;
    padding: 0 14px;
    border-radius: var(--radius-md);
    transition: all var(--transition-fast);
}

:deep(.nav-menu > .el-sub-menu > .el-sub-menu__title:hover) {
    color: var(--color-primary);
    background-color: var(--bg-hover);
}

:deep(.nav-menu > .el-sub-menu.is-active > .el-sub-menu__title) {
    color: var(--color-primary) !important;
}

:deep(.el-sub-menu__icon-arrow) {
    display: none;
}
</style>

<style>
.nav-submenu-popup {
    min-width: 150px !important;
    padding: 4px !important;
    border-radius: 8px !important;
    border: none !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.nav-submenu-popup.el-menu--popup-container {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
}

.nav-submenu-popup .el-menu--popup {
    border: none !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
    border-radius: 8px !important;
    padding: 4px !important;
}

.nav-submenu-popup .el-menu {
    border: none !important;
    background-color: #fff !important;
}

.nav-submenu-popup .el-menu-item {
    font-size: 13px !important;
    height: 36px !important;
    line-height: 36px !important;
    border-radius: 6px !important;
}

/* User dropdown styles */
.user-dropdown {
    min-width: 200px !important;
    padding: 8px 0 !important;
    border-radius: 8px !important;
}

.user-dropdown .user-info {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    gap: 12px;
}

.user-dropdown .avatar-large {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-xs);
    background: var(--text-primary);
    color: var(--bg-card);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 600;
    flex-shrink: 0;
}

.user-dropdown .user-details {
    display: flex;
    flex-direction: column;
}

.user-dropdown .username {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.user-dropdown .role {
    font-size: 12px;
    color: #909399;
    text-transform: capitalize;
}

.user-dropdown .el-dropdown-menu__item {
    padding: 10px 16px !important;
    font-size: 14px !important;
}

.user-dropdown .logout-icon {
    margin-right: 8px;
}

.user-dropdown .el-dropdown-menu__item:hover {
    background-color: #f5f7fa !important;
}
</style>
